/**
 * RefDropdown Component
 *
 * Maps a WorkflowImportPreviewRef to the right cross-module dropdown hook
 * (by `ref.type`) and renders a Select to let the user pick/override the
 * target entity in the current tenant.
 *
 * `funnelStep`/`pipelineStage`:
 *  - with a parent in the file (`ref.dependsOn`, e.g. a node that stores
 *    funnelId + stepId): scoped to the parent chosen above — blocked until it
 *    is chosen, because the parent select IS on the screen;
 *  - without a parent (e.g. a trigger filter "lead.stepId = X", which stores
 *    only the step): ALL steps of the tenant grouped by funnel (one optgroup
 *    per funnel) — there is no parent select to wait for (2026-09-28).
 */

import React, { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Select, type SelectOptionGroup } from '@/shared/components/ui';
import type { WorkflowImportPreviewRef } from '@troia-v3/types';
import { useChannelsDropdown } from '@/modules/channels/hooks/useChannelsDropdown';
import { funnelsApi } from '@/modules/funnels';
import { useFunnelsDropdown, useFunnelStepsDropdown } from '@/modules/funnels/hooks/useFunnels';
import { usePipelinesDropdown, usePipelineStagesDropdown } from '@/modules/helpdesk/pipelines/hooks/usePipelines';
import { pipelinesApi } from '@/modules/helpdesk/pipelines/services/pipelinesApi';
import { useAgentsDropdown } from '@/modules/automation/ai-agents/hooks/useAgentsDropdown';
import { useTemplatesDropdown } from '@/modules/templates/hooks/useTemplatesDropdown';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { useTeamsDropdown } from '@/modules/teams/hooks/useTeams';
import { useContactsDropdown } from '@/modules/contacts/hooks/useContactsDropdown';
import { useDatabasesDropdown } from '@/modules/databases/hooks/useDatabasesDropdown';
import { useFormsDropdown } from '@/shared/hooks/useFormsDropdown';
import { useUnitsDropdown } from '@/shared/hooks/useUnitsDropdown';

interface Props {
  refItem: WorkflowImportPreviewRef;
  value?: string;
  parentValue?: string;
  onChange: (id: string) => void;
}

const LABELS: Record<string, string> = {
  channel: 'Canal',
  funnel: 'Funil',
  funnelStep: 'Etapa',
  pipeline: 'Pipeline',
  pipelineStage: 'Estágio',
  agent: 'Agente',
  template: 'Template',
  user: 'Usuário',
  team: 'Time',
  contact: 'Contato',
  database: 'Base de dados',
  checklistTemplate: 'Modelo de checklist',
  unit: 'Unidade',
  form: 'Formulário',
};

/** Nome do pai que a lista espera, no aviso "escolha o ... acima primeiro". */
const PARENT_LABELS: Record<string, string> = {
  funnelStep: 'o funil',
  pipelineStage: 'o pipeline',
};

interface DropdownOption {
  value: string;
  label: string;
}

interface Child {
  id: string;
  name: string;
  order?: number;
}

function toOpts(arr: Array<{ id: string; name: string }> | undefined): DropdownOption[] {
  return (arr ?? []).map((x) => ({ value: x.id, label: x.name }));
}

/** Um grupo por pai (funil/pipeline), filhos na ordem do quadro. Pai sem filhos não aparece. */
function toGroups(parents: Array<{ id: string; name: string }>, childrenOf: Array<Child[] | undefined>): SelectOptionGroup[] {
  return parents
    .map((parent, i) => ({
      label: parent.name,
      options: [...(childrenOf[i] ?? [])]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((child) => ({ value: child.id, label: child.name })),
    }))
    .filter((group) => group.options.length > 0);
}

/**
 * `funnelStep`/`pipelineStage` que não trouxeram o pai no arquivo: todas as
 * etapas/estágios do tenant, um grupo por funil/pipeline. Só busca quando é
 * esse o caso (uma ida por funil/pipeline, com o mesmo cache das listas
 * escopadas).
 */
function useChildGroups(ref: WorkflowImportPreviewRef): SelectOptionGroup[] {
  const semPai = !ref.dependsOn;
  const stepGroups = ref.type === 'funnelStep' && semPai;
  const stageGroups = ref.type === 'pipelineStage' && semPai;

  const funnels = useFunnelsDropdown({ enabled: stepGroups });
  const pipelines = usePipelinesDropdown({ enabled: stageGroups });
  const funnelList = stepGroups ? funnels.data ?? [] : [];
  const pipelineList = stageGroups ? pipelines.data ?? [] : [];

  const steps = useQueries({
    queries: funnelList.map((funnel) => ({
      queryKey: ['funnel-steps-dropdown', funnel.id],
      queryFn: () => funnelsApi.getStepsByFunnelId(funnel.id),
      staleTime: 10 * 60 * 1000,
    })),
  });
  const stages = useQueries({
    queries: pipelineList.map((pipeline) => ({
      queryKey: ['ticket-stages-dropdown-by-pipeline', pipeline.id],
      queryFn: () => pipelinesApi.getStagesByPipelineId(pipeline.id),
      staleTime: 10 * 60 * 1000,
    })),
  });

  // `useQueries` devolve array novo a cada render — a assinatura marca dado novo.
  const assinatura = [...steps, ...stages].map((q) => q.dataUpdatedAt).join('|');
  return useMemo(() => {
    if (stepGroups) return toGroups(funnelList, steps.map((q) => q.data));
    if (stageGroups) return toGroups(pipelineList, stages.map((q) => q.data));
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `assinatura` cobre os resultados
  }, [stepGroups, stageGroups, funnelList, pipelineList, assinatura]);
}

/**
 * Calls ALL dropdown hooks unconditionally (Rules of Hooks) and selects
 * the right one's data based on `ref.type`. `funnelStep`/`pipelineStage`
 * with a parent are scoped to `parentValue` (the resolved/selected parent id).
 */
function useOptionsForType(ref: WorkflowImportPreviewRef, parentValue?: string): DropdownOption[] {
  const channels = useChannelsDropdown();
  const funnels = useFunnelsDropdown();
  const steps = useFunnelStepsDropdown(ref.type === 'funnelStep' ? parentValue : undefined);
  const pipelines = usePipelinesDropdown();
  const stages = usePipelineStagesDropdown(ref.type === 'pipelineStage' ? parentValue : undefined);
  const agents = useAgentsDropdown();
  const templates = useTemplatesDropdown();
  const users = useUsersDropdown();
  const teams = useTeamsDropdown();
  const contacts = useContactsDropdown();
  const databases = useDatabasesDropdown();
  const forms = useFormsDropdown('form', ref.type === 'form');
  const checklistTemplates = useFormsDropdown('checklist', ref.type === 'checklistTemplate');
  const units = useUnitsDropdown(ref.type === 'unit');

  switch (ref.type) {
    case 'channel':
      return toOpts(channels.data);
    case 'funnel':
      return toOpts(funnels.data);
    case 'funnelStep':
      return toOpts(steps.data);
    case 'pipeline':
      return toOpts(pipelines.data);
    case 'pipelineStage':
      return toOpts(stages.data);
    case 'agent':
      return toOpts(agents.data);
    case 'template':
      return toOpts(templates.data);
    case 'user':
      return toOpts(users.data);
    case 'team':
      return toOpts(teams.data);
    case 'contact':
      return toOpts(contacts.data);
    case 'database':
      return toOpts(databases.data);
    case 'form':
      return forms.data ?? [];
    case 'checklistTemplate':
      return checklistTemplates.data ?? [];
    case 'unit':
      return units.data ?? [];
    default:
      return [];
  }
}

export function RefDropdown({ refItem, value, parentValue, onChange }: Props): React.ReactElement {
  const options = useOptionsForType(refItem, parentValue);
  const groups = useChildGroups(refItem);
  const isChild = refItem.type === 'funnelStep' || refItem.type === 'pipelineStage';
  // Só espera o pai quando o pai EXISTE no arquivo (e, portanto, na tela).
  const blockedByParent = isChild && !!refItem.dependsOn && !parentValue;
  const agrupado = isChild && !refItem.dependsOn;
  const label = `${LABELS[refItem.type] ?? refItem.type}: ${refItem.name ?? '(sem nome)'}${refItem.required ? ' *' : ''}`;

  return (
    <Select
      label={label}
      {...(agrupado ? { groups } : { options })}
      value={value ?? ''}
      onChange={onChange}
      disabled={blockedByParent}
      placeholder={
        blockedByParent
          ? `Escolha ${PARENT_LABELS[refItem.type] ?? 'o item'} acima primeiro`
          : refItem.resolved
          ? 'Encontrado ✓ (pode trocar)'
          : 'Selecione...'
      }
      data-testid={`workflow-import-ref-${refItem.token}`}
    />
  );
}

export default RefDropdown;
